import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';
import { AccountDetailsDialog } from './components/AccountDetails';
import { OrganizerPortalProvider } from './contexts/OrganizerPortalContext';
import Layout from './components/Layout/Layout';
import BottomTabBar from './components/Layout/BottomTabBar';
import FeedbackToast from './components/FeedbackToast';
import HeatWarningBanner from './components/HeatWarningBanner';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RequireOrganizer from './components/Auth/RequireOrganizer';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EventDetail from './pages/EventDetail';
import ChangePassword from './pages/ChangePassword';
import DeleteAccount from './pages/DeleteAccount';
import Landing from './pages/Landing';
import Favorites from './pages/Favorites';
import Profile from './pages/Profile';
import ConfirmEmail from './pages/ConfirmEmail';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import FuerPartner from './pages/FuerPartner';
import FuerVeranstalter from './pages/FuerVeranstalter';
import Winterchallenge from './pages/Winterchallenge';
import WinterchallengeTeam from './pages/WinterchallengeTeam';
import FriendInvite from './pages/FriendInvite';
import WinterchallengeTeamCreate from './pages/WinterchallengeTeamCreate';
import WinterchallengeRangliste from './pages/WinterchallengeRangliste';
import OrganizerRegister from './pages/OrganizerRegister';
import OrganizerAccessApprove from './pages/OrganizerAccessApprove';
import Logo from './pages/Logo';
import UeberUns from './pages/UeberUns';
import Kontakt from './pages/Kontakt';
import Tutorials from './pages/Tutorials';
import ProRaceCalendar from './pages/ProRaceCalendar';
import ProRaceDetail from './pages/ProRaceDetail';
import ProRaceStageDetail from './pages/ProRaceStageDetail';
import RadsportImTV from './pages/RadsportImTV';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import OrganizerList from './pages/OrganizerList';
import OrganizerDetail from './pages/OrganizerDetail';
import NotFound from './pages/NotFound';
import NotificationSettings from './pages/NotificationSettings';
import { EVENT_TYPE_LANDINGS, PERMANENT_LANDING } from './config/seoLandings';
import './App.css';

// ── Admin-/Moderator-Seiten: bewusst per React.lazy in eigene Chunks ────────────────────
// Sie sind nur hinter ProtectedRoute erreichbar, wurden aber statisch importiert und landeten
// damit im einen Haupt-Bundle, das JEDER anonyme Besucher der Landing-/SEO-Seiten laedt. Der
// News-Editor zieht dabei den kompletten TipTap/ProseMirror-Stack mit — hunderte KB, die niemand
// ausserhalb des Admin-Bereichs je braucht und die direkt auf LCP/TBT der Seiten druecken, die
// ranken sollen. Die oeffentlichen Seiten oben bleiben statisch (kein Suspense-Flash).
// Design-Lab (`/design`): die echten Komponenten in allen Zustaenden und Breiten. Wie die
// Admin-Seiten lazy, damit die Beispieldaten nicht im Bundle der oeffentlichen Seiten landen.
const DesignLab = React.lazy(() => import('./pages/DesignLab'));
const ShopApp = React.lazy(() => import('./components/shop/ShopApp'));
const Users = React.lazy(() => import('./pages/Users'));
const Events = React.lazy(() => import('./pages/Events'));
const Organizers = React.lazy(() => import('./pages/Organizers'));
const OrganizerAccess = React.lazy(() => import('./pages/OrganizerAccess'));
const OrganizerSpaceEntry = React.lazy(() => import('./pages/OrganizerSpaceEntry'));
const AdminPrices = React.lazy(() => import('./pages/AdminPrices'));
const AdminAdBanners = React.lazy(() => import('./pages/AdminAdBanners'));
const AdminWinterchallenge = React.lazy(() => import('./pages/AdminWinterchallenge'));
const AdminBookings = React.lazy(() => import('./pages/AdminBookings'));
const AuditLogs = React.lazy(() => import('./pages/AuditLogs'));
const FeedbackPage = React.lazy(() => import('./pages/Feedback'));
const Inquiries = React.lazy(() => import('./pages/Inquiries'));
const TrackingStats = React.lazy(() => import('./pages/TrackingStats'));
const SeoAnalytics = React.lazy(() => import('./pages/SeoAnalytics'));
const BusinessStats = React.lazy(() => import('./pages/BusinessStats'));
const AdminStart = React.lazy(() => import('./pages/AdminStart'));
const EnrichmentQueue = React.lazy(() => import('./pages/EnrichmentQueue'));
const AgentState = React.lazy(() => import('./pages/AgentState'));
const AgentRuns = React.lazy(() => import('./pages/AgentRuns'));
const EventSources = React.lazy(() => import('./pages/EventSources'));
const EmailAdmin = React.lazy(() => import('./pages/EmailAdmin'));
const Rundmail = React.lazy(() => import('./pages/Rundmail'));
const Outreach = React.lazy(() => import('./pages/Outreach'));
const OutreachTemplates = React.lazy(() => import('./pages/OutreachTemplates'));
const ProRaceAdmin = React.lazy(() => import('./pages/ProRaceAdmin'));
const NewsAdmin = React.lazy(() => import('./pages/NewsAdmin'));
const MostFavorited = React.lazy(() => import('./pages/MostFavorited'));
const RegistrationStarts = React.lazy(() => import('./pages/RegistrationStarts'));
const Vorschlaege = React.lazy(() => import('./pages/Vorschlaege'));
const Duplikate = React.lazy(() => import('./pages/Duplikate'));
const EventOverview = React.lazy(() => import('./pages/EventOverview'));
const AdminDocs = React.lazy(() => import('./pages/AdminDocs'));
const ReleaseNotes = React.lazy(() => import('./pages/ReleaseNotes'));

const designLab = (page: React.ReactElement): React.ReactElement =>
  process.env.NODE_ENV === 'production'
    ? <ProtectedRoute requiredRole="ADMIN">{page}</ProtectedRoute>
    : page;

/**
 * Der Bewertungshinweis auf jeder öffentlichen Seite (30.10.2026). Nicht in der Verwaltung, im
 * Design-Lab, bei der Anmeldung und auf Formularseiten wie Passwort oder Konto löschen: dort
 * stört eine Frage nach der Meinung bei etwas anderem.
 */
const OHNE_FEEDBACK = ['/admin', '/moderation', '/events/manage', '/events/review', '/events/duplicates',
  '/events/image-proposals', '/organizers', '/users', '/audit-logs', '/event-sources', '/agent-',
  '/enrichment-queue', '/design', '/login', '/register', '/change-password', '/konto-loeschen',
  '/e-mail-bestaetigen', '/dashboard', '/veranstalter-zugang'];
const FeedbackHinweisGlobal: React.FC = () => {
  const { pathname } = useLocation();
  if (OHNE_FEEDBACK.some(p => pathname === p || pathname.startsWith(p.endsWith('-') ? p : `${p}/`))) return null;
  return <FeedbackToast />;
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
    <Router>
      <AuthProvider>
        <OrganizerPortalProvider>
        <div className="App">
          <ScrollToTop />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'var(--surface-bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--surface-border)',
                boxShadow: '0 4px 16px -4px rgba(0, 0, 0, 0.12)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '10px',
                padding: '12px 16px',
              },
              success: {
                iconTheme: { primary: 'var(--badge-green-text)', secondary: 'white' },
              },
              error: {
                iconTheme: { primary: 'var(--badge-red-text)', secondary: 'white' },
              },
            }}
          />
          <HeatWarningBanner />
          {/* Einmal nach einem neu angelegten Konto, egal über welchen Code-Weg es entstand. */}
          <AccountDetailsDialog />
          {/* Suspense-Grenze fuer die lazy geladenen Admin-Routen. Die oeffentlichen Seiten
              bleiben statisch importiert und rendern unveraendert ohne Fallback-Flash. */}
          <React.Suspense fallback={null}>
          <Routes>
            <Route path="/login" element={<Login key="login" />} />
            <Route path="/register" element={<Login key="register" variant="register" />} />
            <Route path="/e-mail-bestaetigen" element={<ConfirmEmail />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Die Admin-Uebersicht ist am 15.09.2026 entfallen; die Tuer fuehrt ins Tagesgeschaeft. */}
            <Route path="/admin" element={<Navigate to="/admin/start" replace />} />
            <Route
              path="/admin/start"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <AdminStart />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Moderatoren sehen auf /users nur die Zahlen; die Kontenliste blendet die Seite aus. */}
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/manage"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Events />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizers"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Organizers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Vorschlaege und Duplikate sind Sammelseiten mit Reitern; jeder Reiter ist ein Pfad,
                damit Lesezeichen und Badges funktionieren. Die alten Pfade leiten weiter. */}
            <Route path="/moderation/vorschlaege" element={<Navigate to="/moderation/vorschlaege/events" replace />} />
            <Route
              path="/moderation/vorschlaege/:tab"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Vorschlaege />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/events/review" element={<Navigate to="/moderation/vorschlaege/events" replace />} />
            <Route path="/events/image-proposals" element={<Navigate to="/moderation/vorschlaege/bilder" replace />} />
            <Route path="/organizers/review" element={<Navigate to="/moderation/vorschlaege/veranstalter" replace />} />
            {/* Veranstalter-Bereich: nur Anmeldung ueber ProtectedRoute, die Verknuepfung
                prueft RequireOrganizer — ein Veranstalter ist kein Rollenrang. */}
            <Route
              path="/veranstalter-bereich"
              element={
                /* Bewusst OHNE Layout: der Bereich gehoert zur oeffentlichen Seite, die ein
                   Vereinsmensch kennt, nicht in die Seitenleisten-Huelle der Redaktion. Die
                   Seite bringt PublicHeader selbst mit. */
                <ProtectedRoute>
                  <RequireOrganizer>
                    <OrganizerSpaceEntry />
                  </RequireOrganizer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/preise"
              element={
                <ProtectedRoute requiredRole="MODERATOR">
                  <Layout>
                    <AdminPrices />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/winterchallenge"
              element={
                <ProtectedRoute requiredRole="MODERATOR">
                  <Layout>
                    <AdminWinterchallenge />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/werbeplatz"
              element={
                <ProtectedRoute requiredRole="MODERATOR">
                  <Layout>
                    <AdminAdBanners />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/buchungen"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <AdminBookings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizers/zugaenge"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <OrganizerAccess />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/event-sources"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <EventSources />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/moderation/duplikate" element={<Navigate to="/moderation/duplikate/events" replace />} />
            <Route
              path="/moderation/duplikate/:tab"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Duplikate />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/events/duplicates" element={<Navigate to="/moderation/duplikate/events" replace />} />
            <Route path="/organizers/duplicates" element={<Navigate to="/moderation/duplikate/veranstalter" replace />} />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <AuditLogs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <FeedbackPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/anfragen"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Inquiries />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Pfad von vor dem gemeinsamen Posteingang — gesetzte Lesezeichen sollen nicht ins Leere laufen. */}
            <Route path="/admin/partner-anfragen" element={<Navigate to="/admin/anfragen" replace />} />
            <Route
              path="/admin/statistiken"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <TrackingStats />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/auswertung"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <SeoAnalytics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Umsatz, offene Forderung und die Verkaufsliste: Admin allein, anders als die
                uebrige Auswertung. */}
            <Route
              path="/admin/geschaeft"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <BusinessStats />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/enrichment-queue"
              element={
                <ProtectedRoute requiredRole="MODERATOR">
                  <Layout>
                    <EnrichmentQueue />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-state"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <AgentState />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-runs"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <AgentRuns />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/overview"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <EventOverview />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <EmailAdmin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rundmail"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <Rundmail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/outreach"
              element={
                <ProtectedRoute requiredRole="MODERATOR">
                  <Layout>
                    <Outreach />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/outreach/templates"
              element={
                <ProtectedRoute requiredRole="MODERATOR">
                  <Layout>
                    <OutreachTemplates />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/konto-loeschen"
              element={
                <ProtectedRoute>
                  <DeleteAccount />
                </ProtectedRoute>
              }
            />
            <Route path="/events/:id" element={<EventDetail />} />
            {/* SEO-optimierte Kategorie- und Bundesland-Seiten */}
            <Route path="/rennrad-events" element={<Landing forcedCategory="ROAD" />} />
            <Route path="/rennrad-events/:bundesland" element={<Landing forcedCategory="ROAD" />} />
            <Route path="/rtf-events" element={<Landing forcedCategory="ROAD" />} />
            <Route path="/rtf-events/:bundesland" element={<Landing forcedCategory="ROAD" />} />
            <Route path="/gravel-events" element={<Landing forcedCategory="GRAVEL" />} />
            <Route path="/gravel-events/:bundesland" element={<Landing forcedCategory="GRAVEL" />} />
            <Route path="/mtb-events" element={<Landing forcedCategory="MTB" />} />
            <Route path="/mtb-events/:bundesland" element={<Landing forcedCategory="MTB" />} />
            {/* SEO-optimierte Event-Typ-Seiten (programmatic) */}
            {EVENT_TYPE_LANDINGS.map(l => (
              <React.Fragment key={l.slug}>
                <Route path={`/${l.slug}`} element={<Landing forcedEventTypes={l.eventTypes} />} />
                <Route path={`/${l.slug}/:bundesland`} element={<Landing forcedEventTypes={l.eventTypes} />} />
              </React.Fragment>
            ))}
            {/* Permanente Events, gefiltert auf das Merkmal statt auf Eventarten */}
            <Route path={`/${PERMANENT_LANDING.slug}`} element={<Landing forcedPermanent />} />
            <Route path="/radrennen-oesterreich" element={<Landing forcedCountry="AT" />} />
            <Route path="/radrennen-schweiz" element={<Landing forcedCountry="CH" />} />
            <Route path="/events/bundesland/:bundesland" element={<Landing />} />
            <Route path="/events/stadt/:city" element={<Landing />} />
            <Route path="/" element={<Landing />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/community" element={<Navigate to="/favorites?view=friends" replace />} />
            <Route path="/freunde/einladung/:code" element={<FriendInvite />} />
            <Route
              path="/profile/notifications"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/most-favorited"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <MostFavorited />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/anmeldestarts"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <RegistrationStarts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/veranstalter" element={<OrganizerList />} />
            <Route path="/veranstalter/eintragen" element={<OrganizerRegister />} />
            <Route path="/veranstalter/:id" element={<OrganizerDetail />} />
            {/* Der Freischalt-Link aus der Mail an den Verein — ohne Konto erreichbar. */}
            <Route path="/veranstalter-zugang" element={<OrganizerAccessApprove />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/fuer-partner" element={<FuerPartner />} />
            <Route path="/fuer-veranstalter" element={<FuerVeranstalter />} />
            <Route path="/winterchallenge" element={<Winterchallenge />} />
            <Route path="/winterchallenge/team-gruenden" element={<WinterchallengeTeamCreate />} />
            <Route path="/winterchallenge/team/:slug" element={<WinterchallengeTeam />} />
            <Route path="/winterchallenge/rangliste/:kind" element={<WinterchallengeRangliste />} />
            <Route path="/logo" element={<Logo />} />
            <Route path="/ueber-uns" element={<UeberUns />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/rennkalender" element={<ProRaceCalendar />} />
            <Route path="/radsport-im-tv" element={<RadsportImTV />} />
            <Route path="/rennkalender/:slug" element={<ProRaceDetail />} />
            <Route path="/rennkalender/:slug/etappe/:stageNumber" element={<ProRaceStageDetail />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route
              path="/admin/rennkalender"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <ProRaceAdmin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/news"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <NewsAdmin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/handbuch"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <AdminDocs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/neuerungen"
              element={
                <ProtectedRoute requiredRole={['ADMIN', 'MODERATOR']}>
                  <Layout>
                    <ReleaseNotes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Lokal offen, damit `scripts/design-shots.sh` ohne Login fotografieren kann;
                im Prod-Build nur fuer Admins (Abnahme einer Variante vom Handy). */}
            <Route path="/design" element={designLab(<DesignLab />)} />
            <Route path="/design/:entryId" element={designLab(<DesignLab />)} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </React.Suspense>
          <BottomTabBar />
          <FeedbackHinweisGlobal />
        </div>
        </OrganizerPortalProvider>
      </AuthProvider>
    </Router>
    </HelmetProvider>
  );
};

/** Der Shop läuft auf eigener Domain: dieselbe App, aber nichts von Navigation und Konto. */
const isShopHost = /^shop\./.test(window.location.hostname);

const Root: React.FC = () => (isShopHost ? <React.Suspense fallback={null}><ShopApp /></React.Suspense> : <App />);

export default Root;
